// src\app\services\cart\cart.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GlobalService } from '../global/global.service';
import { StorageService } from '../storage/storage.service';
import { FareSettingsService } from '../fare-settings/fare-settings.service';
import { Strings } from '../../enum/strings.enum';
import { Restaurant } from '../../interfaces/restaurant.interface';
import { FareSettings } from '../../interfaces/fare-settings.interface';
import { Cart } from '../../interfaces/cart.interface';
import { Item, ItemVariation, ProductSize } from '../../interfaces/item.interface';
import { Order } from '../../models/order.model';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  model = {} as Cart;

  private _cart = new BehaviorSubject<Cart | null>(null);
  get cart() {
    return this._cart.asObservable();
  }

  constructor(
    private storage: StorageService,
    private global: GlobalService,
    private settings: FareSettingsService
  ) {}

  // --- Internal helpers ---

  /** Round UP to the nearest step (ex: 10 DZD). Prevents under-charging. */
  private roundUpToStep(value: number, step = 10): number {
    const v = Number(value);
    if (!isFinite(v) || v <= 0) return 0;
    const s = Math.max(1, Number(step) || 10);
    return Math.ceil(v / s) * s;
  }

  private getDefaultSize(item: Item): ProductSize | null {
    const sizes = item?.sizes;
    if (!sizes || sizes.length === 0) return null;
    return sizes.find((s: any) => (s as any).is_default) ?? sizes[0] ?? null;
  }

  private normalizeVariation(item: Item, variation?: ItemVariation | null): ItemVariation {
    const defaultSize = this.getDefaultSize(item);

    return {
      ...variation,
      selected_size:
        variation?.selected_size ??
        (item as any).selected_size ??
        defaultSize ??
        null,
      selected_supplements: Array.isArray(variation?.selected_supplements)
        ? variation!.selected_supplements
        : [],
      quantity: typeof variation?.quantity === 'number' && variation.quantity > 0 ? variation.quantity : 1,
    } as ItemVariation;
  }

  private generateVariationId(variation?: ItemVariation | null): string {
    const sizeId = variation?.selected_size?.id || 'default';
    const supplementIds =
      variation?.selected_supplements
        ?.map((s: any) => `${s.id}_${s.selected_quantity ?? 1}`)
        .join('_') || 'no-supplements';
    return `${sizeId}_${supplementIds}`;
  }

  calculateVariationPrice(variation: ItemVariation, item: Item): number {
    const basePrice = Number(variation?.selected_size?.price ?? item.base_price ?? item.price ?? 0);

    const supplementsTotal = Number(
      variation?.selected_supplements?.reduce((sum: number, s: any) => {
        const price = Number(s.price ?? 0);
        const qty = Number(s.selected_quantity ?? 1);
        return sum + price * qty;
      }, 0) ?? 0
    );

    const quantity = Number(variation?.quantity ?? 0);
    return (basePrice + supplementsTotal) * quantity;
  }

  private emitCart() {
    this._cart.next(this.model ? { ...this.model } : null);
  }

  // --- Public API (kept compatible) ---

  isUpdatingCart = false;

  async getCartData() {
    if (this.isUpdatingCart) return;
    this.isUpdatingCart = true;

    try {
      const data = await this.storage.getStorage(Strings.CART_STORAGE);
      if (data?.value) {
        this.model = JSON.parse(data.value);
        await this.calculateCart();
        this.emitCart();
      } else {
        this.model = {} as Cart;
        this._cart.next(null);
      }
    } finally {
      this.isUpdatingCart = false;
    }
  }

  async calculateCart() {
    this.model.totalItem = 0;
    this.model.totalPrice = 0;

    this.model.items = (this.model?.items ?? []).filter((item) =>
      item?.variations?.some((v) => Number(v.quantity) > 0)
    );

    this.model.items.forEach((item) => {
      item.variations = Array.isArray(item.variations) ? item.variations : [];

      item.total_price = item.variations.reduce((sum, variation) => {
        const variationPrice = this.calculateVariationPrice(variation, item);
        variation.total_price = variationPrice;
        return sum + variationPrice;
      }, 0);

      this.model.totalItem! += item.variations.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
      this.model.totalPrice! += Number(item.total_price || 0);
    });

    this.model.grandTotal = this.model.totalPrice;

    if (this.model.totalItem === 0) {
      await this.clearCart();
    }
  }

  async quantityPlus(
    index: number,
    items?: Item[],
    restaurant?: Restaurant,
    variationData?: ItemVariation
  ) {
    if (items) this.model.items = [...items];
    if (restaurant) this.model.restaurant = restaurant;

    const currentItem = this.model.items?.[index];
    if (!currentItem) return;

    currentItem.variations = Array.isArray(currentItem.variations) ? currentItem.variations : [];

    const normalized = this.normalizeVariation(currentItem, variationData);
    const variationId = this.generateVariationId(normalized);

    const existing = currentItem.variations.find((v) => v.id === variationId);

    if (existing) {
      existing.quantity = (existing.quantity || 0) + 1;
      existing.total_price = this.calculateVariationPrice(existing, currentItem);
    } else {
      const newVar: ItemVariation = {
        ...normalized,
        id: variationId,
        quantity: 1,
      };
      newVar.total_price = this.calculateVariationPrice(newVar, currentItem);
      currentItem.variations.push(newVar);
    }

    currentItem.total_price = currentItem.variations.reduce((sum, v) => sum + (v.total_price || 0), 0);

    await this.calculateCart();
    this.emitCart();
  }

  async quantityMinus(index: number, items?: Item[], variationId?: string) {
    const currentItem = items ? items[index] : this.model.items?.[index];
    if (!currentItem?.variations?.length) return;

    const targetId = variationId ?? currentItem.variations[0]?.id;
    if (!targetId) return;

    const variation = currentItem.variations.find((v) => v.id === targetId);
    if (!variation) return;

    variation.quantity = Math.max(0, (variation.quantity || 0) - 1);

    if (variation.quantity === 0) {
      currentItem.variations = currentItem.variations.filter((v) => v.id !== targetId);
    } else {
      variation.total_price = this.calculateVariationPrice(variation, currentItem);
    }

    currentItem.total_price = currentItem.variations.reduce((sum, v) => sum + (v.total_price || 0), 0);

    await this.calculateCart();
    this.emitCart();
  }

  async clearCart() {
    await this.storage.removeStorage(Strings.CART_STORAGE);
    this._cart.next(null);
    this.model = {} as Cart;
  }

  saveCart() {
    this.storage.setStorage(Strings.CART_STORAGE, JSON.stringify(this.model));
  }

  getCart() {
    return this.storage.getStorage(Strings.CART_STORAGE);
  }

  async calculate() {
    await this.calculateCart();
  }

  async updateQuantity(
    index: number,
    items?: Item[],
    restaurant?: Restaurant,
    variationId?: string,
    increment: boolean = true
  ) {
    if (items) this.model.items = [...items];
    if (restaurant) this.model.restaurant = restaurant;

    const currentItem = this.model.items?.[index];
    if (!currentItem?.variations?.length) return;

    const variation = variationId
      ? currentItem.variations.find((v) => v.id === variationId)
      : currentItem.variations[0];

    if (!variation) return;

    variation.quantity = Math.max(0, (variation.quantity || 0) + (increment ? 1 : -1));
    if (variation.quantity === 0) {
      currentItem.variations = currentItem.variations.filter((v) => v.id !== variation.id);
    } else {
      variation.total_price = this.calculateVariationPrice(variation, currentItem);
    }

    currentItem.total_price = currentItem.variations.reduce((sum, v) => sum + (v.total_price || 0), 0);

    await this.calculateCart();
    this.emitCart();
  }

  // async checkCart(current_lat: number, current_lng: number) {
  //   await this.getCartData();
  //   if (!this.model?.restaurant) return false;

  //   const settings = await this.settings.getFareSettings();

  //   const rLat = this.model.restaurant?.g?.geopoint?.latitude;
  //   const rLng = this.model.restaurant?.g?.geopoint?.longitude;

  //   if (typeof rLat !== 'number' || typeof rLng !== 'number') {
  //     return false;
  //   }

  //   const distance = this.global.getDistanceFromLatLngInKm(
  //     current_lat,
  //     current_lng,
  //     rLat,
  //     rLng
  //   );

  //   if (distance > settings.radius) {
  //     this.clearCartAlert();
  //     return true;
  //   }

  //   this.model.distance = this.global.formatNumber(distance);
  //   await this.getDeliveryCharge(settings);
  //   this.emitCart();
  //   return false;
  // }

    async checkCart(current_lat: number, current_lng: number) {
    await this.getCartData();
    if (!this.model?.restaurant) return false;

    // ✅ City-specific settings (fallback to legacy safely)
    const settings = await this.getSettingsForCartRestaurant();

    const ll = this.getRestaurantLatLng();
    if (!ll) return false;

    const distance = this.global.getDistanceFromLatLngInKm(
      current_lat,
      current_lng,
      ll.lat,
      ll.lng
    );

    const radius = Number(settings?.radius ?? 0);
    if (radius > 0 && distance > radius) {
      this.clearCartAlert();
      return true;
    }

    this.model.distance = this.global.formatNumber(distance);
    await this.getDeliveryCharge(settings);
    this.emitCart();
    return false;
  }


  async getDeliveryCharge(settings: FareSettings) {
    const distance = Number(this.model.distance || 0);
    let deliveryCharge = Number(settings.base_amount || 0);

    if (settings.ranges?.length) {
      for (const range of settings.ranges) {
        const start = Number(range.start ?? 0);
        const end = range.end === null ? null : Number(range.end);
        if (distance > start && (end === null || distance <= end)) {
          const effectiveDistance = distance - start;
          deliveryCharge +=
            Number(range.price_per_km || 0) * effectiveDistance + Number(range.variation || 0);
          break;
        }
      }
    } else if (distance > Number(settings.base_distance || 0)) {
      deliveryCharge +=
        (distance - Number(settings.base_distance)) * Number(settings.amount_per_km || 0);
    }

    // ✅ Round delivery fees to nearest 10 DZD (UP)
    const rounded = this.roundUpToStep(deliveryCharge, 10);

    // store as integer-like number (10, 20, 30...)
    this.model.deliveryCharge = rounded;
  }

  saveCartOrder(model: any) {
    this.storage.setStorage(Strings.CART_ORDER, JSON.stringify(model));
  }

  async clearCartOrder() {
    await this.storage.removeStorage(Strings.CART_ORDER);
  }

  getCartOrder() {
    return this.storage.getStorage(Strings.CART_ORDER);
  }

  clearCartAlert() {
  this.clearCart();
  this.global.showAlert(
    'موقعك بعيد جدًا عن المطعم الموجود في سلة التسوق. تم إفراغ السلة، ويرجى البحث عن مطعم آخر قريب.',
    'الموقع بعيد جدًا',
    [{ text: 'حسنًا', role: 'cancel' }]
  );
}


 async alertClearCart(index: number, items: Item[], data: Restaurant, order?: Order) {
  this.global.showAlert(
    // order
      // ? 'هل ترغب في إعادة تعيين سلة التسوق قبل إعادة الطلب من هذا المطعم؟'
      // : 
      'تحتوي سلة التسوق على عناصر من مطعم مختلف. هل ترغب في إعادة تعيين السلة قبل الطلب من هذا المطعم؟',
    'يرجى الإنتباه',
    [
      { text: 'لا', role: 'cancel', handler: () => {} },
      {
        text: 'نعم',
        handler: () => {
          if (order) this.clear(-1, [], {} as Restaurant, order);
          else this.clear(index, items, data);
        },
      },
    ]
  );
}


  async clear(index: number, items: Item[], data: Restaurant, order?: Order) {
    await this.clearCart();
    if (order) {
      this.orderToCart(order);
    } else if (index >= 0 && items.length > 0) {
      this.quantityPlus(index, items, data);
    }
  }

  async orderToCart(order: Order) {
    const data: any = {
      restaurant: order.restaurant,
      items: (order.order || []).map((item: any) => {
        if (!item.variations && typeof item.quantity === 'number') {
          return {
            ...item,
            variations: [
              { id: 'default', quantity: item.quantity, total_price: item.price * item.quantity },
            ],
          };
        }
        return item;
      }),
    };

    this.model = data;
    await this.calculateCart();
    this.saveCart();
    this.emitCart();
    this.global.navigate(['/', 'tabs', 'cart']);
  }

  reset(): void {
    this._cart.next(null);
    this.model = {} as Cart;
    this.isUpdatingCart = false;
  }

    /** Get fare settings for the restaurant city (safe fallback to legacy). */
  private async getSettingsForCartRestaurant(): Promise<FareSettings> {
    const cityId = (this.model?.restaurant as any)?.city;
    if (cityId) {
      return await this.settings.getFareSettingsForCity(String(cityId));
    }
    return await this.settings.getFareSettings(); // legacy fallback
  }

  /** Restaurant coordinates (supports geofirestore 'g.geopoint' or 'coordinates'). */
  private getRestaurantLatLng(): { lat: number; lng: number } | null {
    const r: any = this.model?.restaurant;

    const gp = r?.g?.geopoint;
    if (gp?.latitude != null && gp?.longitude != null) {
      return { lat: Number(gp.latitude), lng: Number(gp.longitude) };
    }

    const c = r?.coordinates;
    if (c?.latitude != null && c?.longitude != null) {
      return { lat: Number(c.latitude), lng: Number(c.longitude) };
    }

    return null;
  }

}
